import * as React from "react";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PdfHighlight {
  colorId: string;
  pageIndex: number;
  left: number;
  top: number;
  width: number;
  height: number;
  text: string;
}

interface PdfSourcePreviewProps {
  href: string;
  title?: string;
  highlights?: PdfHighlight[];
  initialHighlightId: string | null;
}

const PDF_FETCH_TIMEOUT_MS = 45000;
const BASE_PAGE_WIDTH = 760;
const MIN_ZOOM = 0.55;
const MAX_ZOOM = 1.25;
const ZOOM_STEP = 0.1;
const FIT_WIDTH_HORIZONTAL_PADDING = 64;
const ESTIMATED_PAGE_ASPECT_RATIO = 1.294;
const PAGE_RENDER_ROOT_MARGIN = "1200px 0px";
const EMPTY_HIGHLIGHTS: PdfHighlight[] = [];
const HIGHLIGHT_PADDING = {
  horizontal: 0.25,
  vertical: 0.12,
};
const MERGE_VERTICAL_GAP = 1.6;
const MERGE_HORIZONTAL_GAP = 2.1;
const MERGE_HORIZONTAL_OVERLAP_RATIO = 0.05;

export const PdfSourcePreview: React.FC<PdfSourcePreviewProps> = ({
  href,
  title,
  highlights = [],
  initialHighlightId,
}) => {
  const [pdfObjectUrl, setPdfObjectUrl] = React.useState<string | null>(null);
  const [numPages, setNumPages] = React.useState<number>(0);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageInput, setPageInput] = React.useState<string>("1");
  const [zoom, setZoom] = React.useState<number>(1);
  const [useFitWidthZoom, setUseFitWidthZoom] = React.useState<boolean>(true);
  const [isViewerReady, setIsViewerReady] = React.useState<boolean>(false);
  const [activeHighlightId, setActiveHighlightId] = React.useState<string | null>(
    initialHighlightId ?? null
  );
  const [error, setError] = React.useState<string | null>(null);
  const [loadProgress, setLoadProgress] = React.useState<{ loaded: number; total?: number } | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const initialHighlight = React.useMemo(
    () => getInitialHighlight(highlights, initialHighlightId),
    [highlights, initialHighlightId]
  );
  const excerptHighlights = React.useMemo(() => dedupeHighlights(highlights), [highlights]);
  const activeHighlight = React.useMemo(
    () => getActiveHighlight(highlights, activeHighlightId) ?? initialHighlight,
    [activeHighlightId, highlights, initialHighlight]
  );
  const activeHighlightPageIndex = activeHighlight?.pageIndex ?? null;
  const currentPageIndex = Math.max(0, currentPage - 1);
  const highlightsByPage = React.useMemo(() => {
    const grouped = new Map<number, PdfHighlight[]>();
    for (const highlight of highlights) {
      const page = highlight.pageIndex;
      const existing = grouped.get(page);
      if (existing) {
        existing.push(highlight);
      } else {
        grouped.set(page, [highlight]);
      }
    }
    return grouped;
  }, [highlights]);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), PDF_FETCH_TIMEOUT_MS);
    let objectUrl: string | null = null;

    async function fetchPdf(): Promise<void> {
      try {
        const response = await fetch(href, {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`PDF request failed (${response.status})`);
        }

        const totalHeader = response.headers.get("content-length");
        const total = totalHeader ? Number(totalHeader) : undefined;
        const contentType = response.headers.get("content-type") || "application/pdf";
        if (!response.body) {
          const buffer = await response.arrayBuffer();
          if (cancelled) return;
          setLoadProgress({ loaded: buffer.byteLength, total });
          objectUrl = window.URL.createObjectURL(new Blob([buffer], { type: contentType }));
          setPdfObjectUrl(objectUrl);
          return;
        }

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let loaded = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            loaded += value.byteLength;
            if (!cancelled) setLoadProgress({ loaded, total });
          }
        }

        if (cancelled) return;
        const merged = new Uint8Array(loaded);
        let offset = 0;
        for (const chunk of chunks) {
          merged.set(chunk, offset);
          offset += chunk.byteLength;
        }
        objectUrl = window.URL.createObjectURL(new Blob([merged], { type: contentType }));
        setPdfObjectUrl(objectUrl);
      } catch (err) {
        if (cancelled) return;
        const message =
          typeof DOMException !== "undefined" &&
          err instanceof DOMException &&
          err.name === "AbortError"
            ? "Timed out fetching the authenticated PDF."
            : err instanceof Error
              ? err.message
              : String(err);
        setError(message);
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    void fetchPdf();

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [href]);

  const [previousHref, setPreviousHref] = React.useState(href);
  if (previousHref !== href) {
    setPreviousHref(href);
    setPdfObjectUrl(null);
    setNumPages(0);
    setCurrentPage(1);
    setPageInput("1");
    setUseFitWidthZoom(true);
    setIsViewerReady(false);
    setActiveHighlightId(initialHighlightId ?? null);
    setError(null);
    setLoadProgress(null);
  }
  const [previousHighlightId, setPreviousHighlightId] = React.useState(initialHighlightId);
  if (previousHighlightId !== initialHighlightId) {
    setPreviousHighlightId(initialHighlightId);
    setActiveHighlightId(initialHighlightId ?? null);
  }

  const computeFitWidthZoom = React.useCallback((): number => {
    const container = scrollContainerRef.current;
    if (!container) return 1;
    const availableWidth = Math.max(320, container.clientWidth - FIT_WIDTH_HORIZONTAL_PADDING);
    const nextZoom = availableWidth / BASE_PAGE_WIDTH;
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(nextZoom.toFixed(2))));
  }, []);

  React.useEffect(() => {
    if (!useFitWidthZoom) return undefined;

    const applyFitWidthZoom = () => {
      setZoom(computeFitWidthZoom());
    };

    applyFitWidthZoom();

    const container = scrollContainerRef.current;
    if (container && typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(applyFitWidthZoom);
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", applyFitWidthZoom);
    return () => window.removeEventListener("resize", applyFitWidthZoom);
  }, [computeFitWidthZoom, pdfObjectUrl, useFitWidthZoom]);

  React.useEffect(() => {
    if (!pdfObjectUrl) return undefined;
    const id = window.requestAnimationFrame(() => {
      setZoom(computeFitWidthZoom());
      setIsViewerReady(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [computeFitWidthZoom, pdfObjectUrl]);

  const scrollToPage = React.useCallback((page: number, behavior: ScrollBehavior = "smooth") => {
    if (numPages === 0) return;
    const clampedPage = Math.max(1, Math.min(numPages, page));
    setCurrentPage(clampedPage);
    scrollContainerRef.current?.scrollTo({ top: 0, behavior });
  }, [numPages]);

  const scrollToHighlight = React.useCallback((
    highlight: PdfHighlight | null,
    behavior: ScrollBehavior = "smooth"
  ) => {
    if (!highlight || numPages === 0) return;
    setActiveHighlightId(highlight.colorId);
    const pageNumber = highlight.pageIndex + 1;
    scrollToPage(pageNumber, behavior);
  }, [numPages, scrollToPage]);

  const targetPage = initialHighlight && numPages > 0 ? Math.max(1, Math.min(numPages, initialHighlight.pageIndex + 1)) : null;
  const [previousTarget, setPreviousTarget] = React.useState({ targetPage, initialHighlight });
  if (previousTarget.targetPage !== targetPage || previousTarget.initialHighlight !== initialHighlight) {
    setPreviousTarget({ targetPage, initialHighlight });
    if (targetPage !== null && initialHighlight) {
      setCurrentPage(targetPage);
      setActiveHighlightId(initialHighlight.colorId);
    }
  }
  React.useEffect(() => {
    if (targetPage !== null) scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [targetPage, initialHighlight]);

  const displayedPage = String(Math.min(currentPage, Math.max(numPages, 1)));
  const [previousDisplayedPage, setPreviousDisplayedPage] = React.useState(displayedPage);
  if (previousDisplayedPage !== displayedPage) {
    setPreviousDisplayedPage(displayedPage);
    setPageInput(displayedPage);
  }

  const commitPageInput = React.useCallback(() => {
    const parsed = Number(pageInput);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    scrollToPage(parsed);
  }, [currentPage, pageInput, scrollToPage]);

  const progressPercent = React.useMemo(() => {
    if (!loadProgress?.total) return null;
    return Math.max(0, Math.min(100, Math.round((loadProgress.loaded / loadProgress.total) * 100)));
  }, [loadProgress]);
  const displayTitle = React.useMemo(() => {
    const trimmed = title?.trim();
    return trimmed || getPdfDisplayName(href);
  }, [href, title]);
  const showDocumentLoadingOverlay = !error && (!pdfObjectUrl || !isViewerReady || numPages === 0);
  const pageWidth = Math.round(BASE_PAGE_WIDTH * zoom);
  const zoomPercent = Math.round(zoom * 100);
  const statusText = numPages > 0
    ? `Page ${Math.min(currentPage, numPages)} of ${numPages}${highlights.length ? ` · ${highlights.length} highlight${highlights.length === 1 ? "" : "s"}` : ""}`
    : pdfObjectUrl
      ? "Rendering document pages..."
      : "Fetching authenticated document...";
  const zoomOut = () => {
    setUseFitWidthZoom(false);
    setZoom((value) => Math.max(MIN_ZOOM, Number((value - ZOOM_STEP).toFixed(2))));
  };
  const zoomIn = () => {
    setUseFitWidthZoom(false);
    setZoom((value) => Math.min(MAX_ZOOM, Number((value + ZOOM_STEP).toFixed(2))));
  };
  const resetZoom = () => {
    setUseFitWidthZoom(true);
    setZoom(computeFitWidthZoom());
  };

  return (
    <div className="flex h-[640px] min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg shadow-black/5">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background/95 px-3.5 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
            <FileText className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold leading-snug text-foreground">
              {displayTitle}
            </p>
            <p className="truncate text-[11px] text-muted-foreground">{statusText}</p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <div className="inline-flex h-8 items-center overflow-hidden rounded-lg border border-border bg-background shadow-sm">
            <button
              type="button"
              onClick={() => scrollToPage(currentPage - 1)}
              disabled={numPages === 0 || currentPage <= 1}
              className="flex h-8 w-8 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            </button>
            <input
              aria-label="Page number"
              value={pageInput}
              disabled={numPages === 0}
              onChange={(event) => setPageInput(event.target.value)}
              onBlur={commitPageInput}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.currentTarget.blur();
                }
              }}
              className="h-8 w-10 border-x border-border bg-transparent px-1 text-center text-[11px] font-semibold tabular-nums text-foreground outline-none disabled:opacity-50"
            />
            <span className="border-r border-border px-2 text-[11px] text-muted-foreground">
              / {numPages || "-"}
            </span>
            <button
              type="button"
              onClick={() => scrollToPage(currentPage + 1)}
              disabled={numPages === 0 || currentPage >= numPages}
              className="flex h-8 w-8 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Next page"
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>

          <div className="inline-flex h-8 items-center overflow-hidden rounded-lg border border-border bg-background shadow-sm">
            <button
              type="button"
              onClick={zoomOut}
              disabled={zoom <= MIN_ZOOM}
              className="flex h-8 w-8 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className="h-8 min-w-12 border-x border-border px-2 text-center text-[11px] font-semibold tabular-nums text-foreground transition-colors hover:bg-accent"
              aria-label="Fit page to available width"
            >
              {zoomPercent}%
            </button>
            <button
              type="button"
              onClick={zoomIn}
              disabled={zoom >= MAX_ZOOM}
              className="flex h-8 w-8 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      <div className="flex min-h-[520px] flex-1 overflow-hidden">
        {excerptHighlights.length > 0 && (
          <PdfExcerptsRail
            highlights={excerptHighlights}
            activeHighlightId={activeHighlightId}
            onSelect={scrollToHighlight}
          />
        )}
        <div
          ref={scrollContainerRef}
          className="relative min-w-0 flex-1 overflow-y-scroll bg-[radial-gradient(circle_at_top,hsl(var(--muted)/0.55),hsl(var(--muted)/0.18))] px-4 py-5 [scrollbar-gutter:stable]"
        >
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
              Couldn&apos;t render the PDF preview.
              <div className="mt-1 opacity-80">{error}</div>
            </div>
          )}
          {showDocumentLoadingOverlay && (
            <div className="absolute inset-0 z-20 bg-background">
              <PdfLoadingState progressPercent={progressPercent} />
            </div>
          )}
          {pdfObjectUrl && isViewerReady && (
            <Document
              file={pdfObjectUrl}
              loading={null}
              error={null}
              onLoadSuccess={({ numPages: nextNumPages }) => {
                setError(null);
                setNumPages(nextNumPages);
                setLoadProgress(null);
              }}
              onLoadError={(err) => {
                setNumPages(0);
                setError(err instanceof Error ? err.message : String(err));
                setLoadProgress(null);
              }}
            >
              <div
                className={[
                  "flex flex-col items-center gap-8",
                  numPages > 0 ? "visible" : "invisible",
                ].join(" ")}
              >
                <div className="flex w-full flex-col items-center gap-2.5">
                  {activeHighlightPageIndex === currentPageIndex && (
                    <div className="flex max-w-full justify-end px-1" style={{ width: pageWidth }}>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200">
                        Active citation
                      </span>
                    </div>
                  )}
                  <PdfPageSlot
                    key={currentPage}
                    pageNumber={currentPage}
                    pageWidth={pageWidth}
                    rootRef={scrollContainerRef}
                    forceRender
                    highlights={highlightsByPage.get(currentPageIndex) ?? EMPTY_HIGHLIGHTS}
                    activeId={activeHighlightId}
                  />
                </div>
              </div>
            </Document>
          )}
        </div>
      </div>
    </div>
  );
};

const PdfLoadingState: React.FC<{ progressPercent: number | null }> = ({ progressPercent }) => (
  <div className="flex h-full min-h-[520px] w-full flex-col items-center justify-center gap-3 bg-background px-4 py-10 text-center">
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
    </div>
    <div>
      <p className="text-[13px] font-semibold text-foreground">Preparing PDF preview</p>
      <p className="mt-1 text-[12px] text-muted-foreground">
        Fetching the document and rendering pages.
      </p>
    </div>
    {progressPercent !== null && (
      <div className="w-full max-w-[280px]">
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">{progressPercent}% loaded</p>
      </div>
    )}
  </div>
);

const PdfPageLoadingState: React.FC<{ pageNumber: number; width: number }> = ({ pageNumber, width }) => (
  <div
    className="flex h-[520px] max-w-full flex-col items-center justify-center gap-2 rounded-lg bg-white shadow-sm"
    style={{ width }}
  >
    <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden />
    <p className="text-[11px] text-muted-foreground">Rendering page {pageNumber}…</p>
  </div>
);

const PdfExcerptsRail: React.FC<{
  highlights: PdfHighlight[];
  activeHighlightId: string | null;
  onSelect: (
    highlight: PdfHighlight,
    behavior?: ScrollBehavior
  ) => void;
}> = ({ highlights, activeHighlightId, onSelect }) => (
  <aside className="flex w-[280px] shrink-0 flex-col border-r border-border bg-background/95">
    <div className="border-b border-border px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Cited excerpts
      </p>
      <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
        Select an excerpt to jump to the cited passage in the PDF.
      </p>
    </div>
    <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
      <ol className="flex flex-col gap-2">
        {highlights.map((highlight, index) => {
          const isActive = Boolean(
            activeHighlightId &&
              (highlight.colorId === activeHighlightId || highlight.text?.includes(activeHighlightId))
          );
          return (
            <li key={`${highlight.colorId}-${index}`}>
              <button
                type="button"
                onClick={() => onSelect(highlight)}
                className={[
                  "w-full cursor-pointer rounded-lg border px-3 py-2.5 text-left transition-colors",
                  isActive
                    ? "border-amber-300 bg-amber-50 text-amber-950 shadow-sm"
                    : "border-border bg-card text-foreground hover:border-primary/30 hover:bg-accent/40",
                ].join(" ")}
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Page {highlight.pageIndex + 1}
                  </span>
                  {isActive && (
                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">
                      Active
                    </span>
                  )}
                </div>
                <p className="line-clamp-4 text-[12px] leading-relaxed">
                  {highlight.text || "Cited passage"}
                </p>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  </aside>
);

const PdfPageSlot: React.FC<{
  pageNumber: number;
  pageWidth: number;
  rootRef: React.RefObject<HTMLDivElement | null>;
  forceRender: boolean;
  highlights: PdfHighlight[];
  activeId: string | null;
  onRenderSuccess?: () => void;
}> = ({ pageNumber, pageWidth, rootRef, forceRender, highlights, activeId, onRenderSuccess }) => {
  const slotRef = React.useRef<HTMLDivElement | null>(null);
  const [isNearViewport, setIsNearViewport] = React.useState(forceRender);
  const estimatedHeight = Math.round(pageWidth * ESTIMATED_PAGE_ASPECT_RATIO);
  const shouldRender = forceRender || isNearViewport;

  if (forceRender && !isNearViewport) setIsNearViewport(true);

  React.useEffect(() => {
    if (forceRender) return undefined;
    const root = rootRef.current;
    const element = slotRef.current;
    if (!root || !element || typeof IntersectionObserver === "undefined") {
      setIsNearViewport(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsNearViewport(entries.some((entry) => entry.isIntersecting));
      },
      { root, rootMargin: PAGE_RENDER_ROOT_MARGIN }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [forceRender, rootRef]);

  return (
    <div
      ref={slotRef}
      className="relative overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black/5"
      data-pdf-page-content
      style={!shouldRender ? { width: pageWidth, height: estimatedHeight } : undefined}
    >
      {shouldRender ? (
        <>
          <Page
            pageNumber={pageNumber}
            width={pageWidth}
            loading={<PdfPageLoadingState pageNumber={pageNumber} width={pageWidth} />}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onRenderSuccess={onRenderSuccess}
          />
          <PdfHighlightOverlay highlights={highlights} activeId={activeId} />
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-white">
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
            <div className="h-full w-1/2 rounded-full bg-primary/40" />
          </div>
        </div>
      )}
    </div>
  );
};

function getInitialHighlight(
  highlights: PdfHighlight[],
  currentId: string | null
): PdfHighlight | null {
  if (!highlights.length) return null;
  const direct = currentId
    ? highlights.find((highlight) => highlight.colorId === currentId || highlight.text?.includes(currentId))
    : null;
  return direct ?? highlights[0];
}

function getActiveHighlight(
  highlights: PdfHighlight[],
  currentId: string | null
): PdfHighlight | null {
  if (!currentId) return null;
  return (
    highlights.find((highlight) => highlight.colorId === currentId || highlight.text?.includes(currentId)) ??
    null
  );
}

function dedupeHighlights(
  highlights: PdfHighlight[]
): PdfHighlight[] {
  const seen = new Set<string>();
  const out: PdfHighlight[] = [];
  for (const highlight of highlights) {
    const key = `${highlight.pageIndex}:${highlight.text || highlight.colorId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(highlight);
  }
  return out;
}

function getPdfDisplayName(href: string): string {
  const proxiedPrefix = "/api/gcpfile/";
  const rawUrl = href.includes(proxiedPrefix)
    ? href.slice(href.indexOf(proxiedPrefix) + proxiedPrefix.length)
    : href;
  const decoded = (() => {
    try {
      return decodeURIComponent(rawUrl);
    } catch {
      return rawUrl;
    }
  })();

  const withoutQuery = decoded.split(/[?#]/)[0];
  const fileName = withoutQuery.split(/[\\/]/).filter(Boolean).pop();
  return fileName || "Source PDF";
}

const PdfHighlightOverlay: React.FC<{
  highlights: PdfHighlight[];
  activeId: string | null;
}> = ({ highlights, activeId }) => {
  if (highlights.length === 0) return null;
  const visibleHighlights = activeId
    ? highlights.filter(
        (highlight) => highlight.colorId === activeId || highlight.text?.includes(activeId)
      )
    : [];
  if (visibleHighlights.length === 0) return null;
  const mergedHighlights = mergePdfHighlightAreas(visibleHighlights);

  return (
    <div className="pointer-events-none absolute inset-0">
      {mergedHighlights.map((highlight, idx) => (
        <div
          key={`${highlight.colorId}-${idx}`}
          className="absolute rounded-[3px]"
          style={{
            left: `${highlight.left}%`,
            top: `${highlight.top}%`,
            width: `${highlight.width}%`,
            height: `${highlight.height}%`,
            backgroundColor: "rgba(255, 235, 59, 0.28)",
            boxShadow: "0 0 0 1px rgba(245, 158, 11, 0.28)",
          }}
          title={highlight.text}
        />
      ))}
    </div>
  );
};

interface PercentRect {
  colorId: string;
  text?: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
}

function mergePdfHighlightAreas(
  highlights: PdfHighlight[]
): Array<{ colorId: string; text?: string; left: number; top: number; width: number; height: number }> {
  const rects = highlights
    .map((highlight): PercentRect => ({
      colorId: highlight.colorId,
      text: highlight.text,
      left: highlight.left,
      top: highlight.top,
      right: highlight.left + highlight.width,
      bottom: highlight.top + highlight.height,
    }))
    .sort((a, b) => a.top - b.top || a.left - b.left);

  const merged: PercentRect[] = [];
  for (const rect of rects) {
    let mergedIntoExisting = false;
    for (let i = 0; i < merged.length; i++) {
      if (shouldMergePdfRects(merged[i], rect)) {
        merged[i] = unionPdfRects(merged[i], rect);
        mergedIntoExisting = true;
        break;
      }
    }
    if (!mergedIntoExisting) merged.push(rect);
  }

  let didMerge = true;
  while (didMerge) {
    didMerge = false;
    for (let i = 0; i < merged.length; i++) {
      for (let j = i + 1; j < merged.length; j++) {
        if (shouldMergePdfRects(merged[i], merged[j])) {
          merged[i] = unionPdfRects(merged[i], merged[j]);
          merged.splice(j, 1);
          didMerge = true;
          break;
        }
      }
      if (didMerge) break;
    }
  }

  return merged.map((rect) => {
    const left = Math.max(0, rect.left - HIGHLIGHT_PADDING.horizontal);
    const top = Math.max(0, rect.top - HIGHLIGHT_PADDING.vertical);
    const right = Math.min(100, rect.right + HIGHLIGHT_PADDING.horizontal);
    const bottom = Math.min(100, rect.bottom + HIGHLIGHT_PADDING.vertical);
    return {
      colorId: rect.colorId,
      text: rect.text,
      left,
      top,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
    };
  });
}

function shouldMergePdfRects(a: PercentRect, b: PercentRect): boolean {
  if (a.colorId !== b.colorId) return false;
  const verticalOverlap = b.top <= a.bottom && b.bottom >= a.top;
  const verticalGap = b.top - a.bottom;
  const verticalClose = verticalOverlap || verticalGap <= MERGE_VERTICAL_GAP;

  const overlap = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const minWidth = Math.min(a.right - a.left, b.right - b.left);
  const overlapRatio = minWidth > 0 ? overlap / minWidth : 0;
  const horizontalClose =
    overlap > 0
      ? overlapRatio >= MERGE_HORIZONTAL_OVERLAP_RATIO
      : b.left <= a.right + MERGE_HORIZONTAL_GAP &&
        b.right >= a.left - MERGE_HORIZONTAL_GAP;

  return verticalClose && horizontalClose;
}

function unionPdfRects(a: PercentRect, b: PercentRect): PercentRect {
  return {
    colorId: a.colorId,
    text: a.text || b.text,
    left: Math.min(a.left, b.left),
    top: Math.min(a.top, b.top),
    right: Math.max(a.right, b.right),
    bottom: Math.max(a.bottom, b.bottom),
  };
}
